import {
  dateRangeFromISO,
  dateRangeMonths,
  getDateRange,
} from "@app/lib/date-range";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { loadQuery } from "react-relay";

// Import the List component we just created
import StakeholdersList, {
  PAGE_SIZE,
  stakeholdersQuery,
} from "@app/components/stakeholders-list";
import type { stakeholdersList_StakeholdersListQuery } from "@app/queries/stakeholdersList_StakeholdersListQuery.graphql";
import { environment } from "@app/relay/relay-environment";
import { pageHead } from "@app/lib/page-title";

export const Route = createFileRoute("/_authed/stakeholders")({
  loader: () => {
    // The per-stakeholder cost sparklines follow the global date range through
    // today. "All time" has no ISO lower bound; without one the range ran back
    // to 1900, so each row rendered a ~1,500-column monthly sparkline (50 rows ≈
    // 75k cells) and the page took many seconds. Bound "All time" to the app's
    // own 120-month convention (dateRangeMonths) — data only goes back ~2018, so
    // no rows are lost and the payload/DOM shrink ~12×.
    const range = getDateRange();
    const monthsAgo = new Date();
    monthsAgo.setMonth(monthsAgo.getMonth() - dateRangeMonths(range));
    const initialStart =
      dateRangeFromISO(range) ?? monthsAgo.toISOString().slice(0, 10);
    const initialEnd = new Date().toISOString().slice(0, 10);

    return loadQuery<stakeholdersList_StakeholdersListQuery>(
      environment,
      stakeholdersQuery,
      {
        search: null,
        limit: PAGE_SIZE,
        offset: 0,
        initialStart,
        initialEnd,
      },
      { fetchPolicy: "store-or-network" }
    );
  },
  // Ensure this points to the function defined below
  head: () => pageHead("Stakeholders"),
  component: StakeholdersPage,
});

// Define the component here
function StakeholdersPage() {
  const queryRef = Route.useLoaderData();

  return (
    <div className="p-4">
      <h3 className="text-xl font-bold mb-4">Stakeholders</h3>

      <ErrorBoundary
        fallback={<div className="text-red-500">Error loading data!</div>}
      >
        <Suspense
          fallback={<div className="text-blue-500">Loading list...</div>}
        >
          {/* Render the separate list component */}
          <StakeholdersList queryRef={queryRef} />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
